/**
 * General-info fields for the always-editable detail Overview tab.
 *
 * Covers: Essentials, Contact, Tax, Location, Deposit.
 * Pricing / payment-methods / late-fee live in <PropertyPricingFields> (the
 * Pricing tab). Mirrors the <PropertyGeneralFields> standalone grouping used
 * by the create form.
 */

import { useTranslation } from 'react-i18next'
import { Controller, type Control, type UseFormRegister, type FieldErrors, type UseFormWatch } from 'react-hook-form'
import { Card, Input, Select, Toggle, Badge } from '@/components/ui'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { resolveAccountBookingSettings } from '@/lib/bookingSettings'
import { formatBaht } from '@/lib/money'
import type { PropertyFormData } from '@/lib/schemas'

export interface PropertyFormFieldsProps {
  // RHF bindings — parents own form state
  register: UseFormRegister<PropertyFormData>
  errors: FieldErrors<PropertyFormData>
  watch: UseFormWatch<PropertyFormData>
  control: Control<PropertyFormData>
}

export function PropertyFormFields({
  register,
  errors,
  watch,
  control,
}: PropertyFormFieldsProps) {
  const { t } = useTranslation('properties')

  const depositType = watch('deposit_type')

  // EP-111 T111-7 — the canonical single-gate accessor (T111-6). No Unleash
  // flag exists for condominium; standalone-form-only (Q4 — this component
  // is only consumed by the always-editable /properties/:id Overview tab,
  // never the onboarding wizard).
  const { data: accountSettings } = useAccountSettings()
  const condominiumEnabled = accountSettings?.settings?.condominium_enabled === true

  // EP-136 (T136-026) — per-key override flags for the Booking block below.
  // Each pair (`*_override` + the value field) is independent — the property
  // can override booking_fee_default alone while still inheriting
  // booking_fee_mode from the account (Property#resolved_booking_settings
  // merges per key, never as a whole-hash shadow).
  const bookingFeeModeOverride = watch('booking_fee_mode_override')
  const bookingFeeDefaultOverride = watch('booking_fee_default_override')
  const bookingFeeForfeitOverride = watch('booking_fee_forfeit_on_cancel_override')
  const resolvedAccountBooking = resolveAccountBookingSettings(accountSettings?.settings?.booking_settings)

  return (
    <>
      {/* Essentials — name + billing_day. Mirrors <PropertyGeneralFields>'s
          standalone-mode grouping so the always-editable detail Overview tab
          matches the create form (EP-86 "regroup everywhere"). */}
      <Card title={t('sections.essentials')}>
        <div className="grid gap-4">
          <Input
            label={t('labels.property_name')}
            placeholder={t('placeholders.property_name')}
            error={errors.name?.message}
            required
            {...register('name')}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2 mt-4 [&>*]:min-w-0">
          <Select
            label={t('labels.billing_day')}
            required
            options={Array.from({ length: 28 }, (_, i) => ({
              value: String(i + 1),
              label: t('labels.billing_day_option', { day: i + 1 }),
            }))}
            {...register('billing_day', { valueAsNumber: true })}
          />
          <Controller
            name="payment_due_day"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label={t('labels.payment_due_day')}
                error={errors.payment_due_day?.message}
                options={[
                  { value: '', label: t('labels.payment_due_day_blank') },
                  ...Array.from({ length: 31 }, (_, i) => ({
                    value: String(i + 1),
                    label: t('labels.payment_due_day_option', { day: i + 1 }),
                  })),
                ]}
                value={field.value ?? ''}
                // Clearing the selection must clear the saved day on PATCH.
                onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
              />
            )}
          />
          {/* EP-111 T111-7 — reuses the leftover property_types i18n keys
              (predate the EP-58 column drop). */}
          {condominiumEnabled && (
            <Select
              label={t('labels.property_type')}
              options={[
                { value: 'apartment', label: t('property_types.apartment') },
                { value: 'condominium', label: t('property_types.condominium') },
              ]}
              {...register('property_type')}
            />
          )}
        </div>
        <p className="text-sm text-base-content/60 mt-3">
          {t('sections.billing_settings_hint')}
        </p>
      </Card>

      {/* Billing Settings — deposit_type + deposit_value. Sits directly below
          Essentials so all billing config (billing_day + deposit) is grouped. */}
      <Card title={t('sections.billing_settings')}>
        <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
          <Select
            label={t('labels.deposit_type')}
            options={[
              { value: 'months', label: t('deposit_types.months') },
              { value: 'fixed', label: t('deposit_types.fixed') },
            ]}
            {...register('deposit_type')}
          />
          <Input
            label={depositType === 'months' ? t('labels.deposit_value_months') : t('labels.deposit_value_fixed')}
            type="number"
            placeholder={depositType === 'months' ? '1' : '5000'}
            {...register('deposit_value', { valueAsNumber: true })}
          />
        </div>
      </Card>

      {/* Booking — EP-136 (T136-026). Sits directly below Billing Settings,
          the closest analogue (both are per-lease money defaults). Each of
          the 3 keys shows its own inherited-vs-overridden state per the
          per-key merge in Property#resolved_booking_settings — a single
          whole-block toggle would misrepresent what the server actually
          does (see the payment_methods override precedent's doc-comment in
          property.rb for why that pattern is explicitly avoided here). */}
      <Card title={t('sections.booking')}>
        <p className="text-sm text-base-content/60 mb-4">{t('booking.description')}</p>
        <div className="grid gap-6 md:grid-cols-2 [&>*]:min-w-0">
          {/* Booking fee treatment (deposit | revenue) */}
          <div data-testid="booking-fee-mode-field">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-sm font-medium">{t('booking.fee_mode_label')}</span>
              <div className="flex items-center gap-2">
                <Badge variant={bookingFeeModeOverride ? 'primary' : 'ghost'}>
                  {bookingFeeModeOverride ? t('booking.overridden_badge') : t('booking.inherited_badge')}
                </Badge>
                <Toggle
                  size="sm"
                  aria-label={`${t('booking.fee_mode_label')} — ${t('booking.override_toggle_label')}`}
                  {...register('booking_fee_mode_override')}
                />
              </div>
            </div>
            {bookingFeeModeOverride ? (
              <Select
                aria-label={t('booking.fee_mode_label')}
                options={[
                  { value: 'deposit', label: t('booking.fee_mode_options.deposit') },
                  { value: 'revenue', label: t('booking.fee_mode_options.revenue') },
                ]}
                {...register('booking_fee_mode')}
              />
            ) : (
              <p className="text-sm text-base-content/60">
                {t('booking.inherited_value', { value: t(`booking.fee_mode_options.${resolvedAccountBooking.mode}`) })}
              </p>
            )}
          </div>

          {/* Default booking amount */}
          <div data-testid="booking-fee-default-field">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-sm font-medium">{t('booking.fee_default_label')}</span>
              <div className="flex items-center gap-2">
                <Badge variant={bookingFeeDefaultOverride ? 'primary' : 'ghost'}>
                  {bookingFeeDefaultOverride ? t('booking.overridden_badge') : t('booking.inherited_badge')}
                </Badge>
                <Toggle
                  size="sm"
                  aria-label={`${t('booking.fee_default_label')} — ${t('booking.override_toggle_label')}`}
                  {...register('booking_fee_default_override')}
                />
              </div>
            </div>
            {bookingFeeDefaultOverride ? (
              <Input
                aria-label={t('booking.fee_default_label')}
                type="number"
                min={0}
                step="0.01"
                leftIcon={<span className="text-base-content/60">฿</span>}
                error={errors.booking_fee_default?.message}
                {...register('booking_fee_default', { valueAsNumber: true })}
              />
            ) : (
              <p className="text-sm text-base-content/60">
                {resolvedAccountBooking.default !== undefined
                  ? t('booking.inherited_value', { value: formatBaht(resolvedAccountBooking.default) })
                  : t('booking.fee_default_not_set')}
              </p>
            )}
          </div>
        </div>

        {/* Forfeit on cancel — full width, boolean */}
        <div className="mt-4 pt-4 border-t border-base-200" data-testid="booking-fee-forfeit-field">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="text-sm font-medium">{t('booking.forfeit_label')}</span>
            <div className="flex items-center gap-2">
              <Badge variant={bookingFeeForfeitOverride ? 'primary' : 'ghost'}>
                {bookingFeeForfeitOverride ? t('booking.overridden_badge') : t('booking.inherited_badge')}
              </Badge>
              <Toggle
                size="sm"
                aria-label={`${t('booking.forfeit_label')} — ${t('booking.override_toggle_label')}`}
                {...register('booking_fee_forfeit_on_cancel_override')}
              />
            </div>
          </div>
          {bookingFeeForfeitOverride ? (
            <Toggle
              variant="primary"
              aria-label={t('booking.forfeit_label')}
              {...register('booking_fee_forfeit_on_cancel')}
            />
          ) : (
            <p className="text-sm text-base-content/60">
              {t('booking.inherited_value', {
                value: resolvedAccountBooking.forfeitOnCancel
                  ? t('booking.forfeit_forfeited')
                  : t('booking.forfeit_refunded'),
              })}
            </p>
          )}
        </div>
      </Card>

      {/* Contact — phone + email */}
      <Card title={t('sections.contact')}>
        <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
          <Input
            label={t('labels.phone')}
            placeholder={t('placeholders.phone')}
            {...register('phone')}
          />
          <Input
            label={t('labels.email')}
            placeholder={t('placeholders.email')}
            type="email"
            {...register('email')}
          />
        </div>
      </Card>

      {/* Tax — tax_id + branch_number */}
      <Card title={t('sections.tax')}>
        <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
          <Input
            label={t('labels.tax_id')}
            placeholder={t('placeholders.tax_id')}
            {...register('tax_id')}
          />
          <Input
            label={t('labels.branch_number')}
            placeholder={t('placeholders.branch_number')}
            {...register('branch_number')}
          />
        </div>
      </Card>

      {/* Location — address + location_url */}
      <Card title={t('sections.location')}>
        <div className="grid gap-4">
          <Input
            label={t('labels.address')}
            placeholder={t('placeholders.address')}
            {...register('address')}
          />
          <Input
            label={t('labels.location_url')}
            placeholder={t('placeholders.location_url')}
            type="url"
            error={errors.location_url?.message}
            {...register('location_url')}
          />
        </div>
      </Card>
    </>
  )
}
